import { type PropType, nextTick, provide, defineComponent, h, type SlotsType, inject, computed, mergeProps } from 'vue';
import type { Method, RequestError } from '../../Http/Client/Request';
import { CompleteResponse } from '../../Http/Client/Response';
import { useHttpClient } from '../../Composables/UseHttpClient';
import { createFormContext, FormContextInjectionKey, type FormContextInterface } from '../../Services/FormContext';
import { useErrors } from '../../Services/StateManager.ts';
import lodashMerge from 'lodash.merge';
import { buildFormData } from '../../Support/FormData.ts';
import { useUrlGenerator } from '../../Services/UrlGenerator.ts';

type FormHandler = (data: any, ctx: FormContextInterface) => Promise<any>;

export const FormControllerComponent = defineComponent({
  name: 'FormController',
  props: {
    action: {
      type: String,
      required: false,
    },
    method: {
      type: String as PropType<Method>,
      required: false,
      default: 'POST',
    },
    data: {
      type: Object,
      required: false,
      default: {},
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    continuous: {
      type: Boolean,
      required: false,
      default: false,
    },
    bag: {
      type: String,
      required: false,
      default: 'default',
    },
    onSubmit: {
      type: Function as PropType<FormHandler>,
      required: false,
    },
  },
  slots: Object as SlotsType<{
    default: {
      data: any
      processing: boolean,
      errors: Record<string, string[]>,
      touched: Record<string, boolean>,
      ctx: FormContextInterface,
      submit: () => void,
    },
  }>,
  setup(props, { attrs, slots, expose }) {
    const parent = inject(FormContextInjectionKey, null);

    const ctx = createFormContext(() => props.data, () => props.bag, () => props.readonly);
    const http = useHttpClient();
    const url = useUrlGenerator();
    const bags = useErrors();

    const { data, files, processing, readonly, errors, touched } = ctx;

    const element = computed(() => {
      return parent ? 'div' : 'form';
    });

    const specific = computed(() => {
      return parent ? {
        ['data-action']: props.action,
        ['data-method']: props.method,
      } : {
        ['action']: props.action,
        ['method']: props.method,
      };
    });

    function dispatch() {
      if (props.onSubmit) {
        return props.onSubmit(data.value, ctx);
      }

      if (!props.action) {
        throw new Error('You must either provide action or your custom form handler!');
      }

      if (props.method === 'GET') {
        return http.dispatch(props.method, url.generate(props.action, data.value));
      }

      let method = props.method;
      let fields = lodashMerge(data.value, files.value);

      // When files are defined in the form, convert form data from JSON into
      // FormData. This allows the form to be sent as "multipart/form-data",
      // which supports files upload.
      if (Object.keys(files.value).length > 0) {
        // When form method isn't POST, implement a method spoofing mechanism
        // supported by Laravel. This allows the framework to treat, for example,
        // a POST request as PATCH. It's due to the specific of PHP as it only
        // allows file uploads within the POST method.
        if (method !== 'POST') {
          fields._method = props.method;
          method = 'POST';
        }

        fields = buildFormData(fields);
      }

      return http.dispatch(method, props.action, { data: fields });
    }

    function submit(event?: Event) {
      let beforeReadonly = readonly.value;

      if (event) {
        event.stopPropagation();
        event.preventDefault();
      }

      processing.value = true;

      if (!props.continuous) {
        readonly.value = true;
      }

      bags.value = {};
      touched.value = {};

      // noinspection JSIgnoredPromiseFromCall
      nextTick(() => {
        dispatch().catch((error: RequestError) => {
          if (error.response instanceof CompleteResponse) {
            let response = error.response;

            bags.value = response.errors;

            if (!props.continuous) {
              nextTick().then(() => {
                document.querySelector('.control--error .control__component')?.scrollIntoView({
                  behavior: 'instant',
                  block: 'center',
                });
              });
            }

            if (response.previous) {
              nextTick().then(() => {
                data.value = lodashMerge(data.value, response.previous);
              });
            }
          }
        }).finally(() => {
          processing.value = false;
          readonly.value = beforeReadonly;
        });
      });
    }

    function handleSubmit(event: Event) {
      event.stopPropagation();
      event.preventDefault();

      submit();
    }

    function onKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const target = event.target as HTMLElement;

        if (target.tagName !== 'TEXTAREA' && !(target as HTMLInputElement).form && element.value === 'div') {
          handleSubmit(event);
        }
      }
    }

    const eventHandlers = computed(() => {
      let handlers = {} as any;

      if (element.value === 'form') {
        handlers = { onSubmit: handleSubmit };
      } else if (element.value === 'div') {
        // noinspection JSUnusedGlobalSymbols
        handlers = { onKeydown };
      }

      if (props.continuous) {
        handlers.onChange = handleSubmit;
      }

      return handlers;
    });

    expose({
      ctx,
      submit,
    });

    provide(FormContextInjectionKey, ctx);

    return () => h(element.value, mergeProps(attrs, specific.value, eventHandlers.value, { class: 'form' }), slots.default({
      data: data.value,
      processing: processing.value,
      errors: errors.value,
      touched: touched.value,
      ctx,
      submit,
    }));
  },
});
